import type { AccountProps } from '../accounts/accountsTypes';
import { isQBPAccount } from '../accounts/accountsUtils';
import type { ChartOfAccountProps } from '../chart-of-accounts/chartOfAccountsTypes';
import type { CategoryProps } from './categoriesTypes';

export const UNCATEGORIZED_COA: ChartOfAccountProps = { type: 'UNCATEGORIZED', id: '0' };

type CategoryLookup = { get(id: string): CategoryProps | undefined };
type AccountLookup = {
  get(id: string): AccountProps | undefined;
  find(predicate: (value: AccountProps, key?: unknown, iter?: unknown) => boolean): AccountProps | undefined;
};

export const shouldResetCategoryForBusinessChange = ({
  coa,
  category,
  nextBusinessId,
}: {
  coa: ChartOfAccountProps | null | undefined;
  category: CategoryProps | null | undefined;
  nextBusinessId: string | undefined;
}): boolean => {
  if (nextBusinessId === undefined) return false;
  if (coa?.type !== 'CATEGORY' || !coa.id) return false;
  if (!category) return false;
  if (category.usageType === 'PERSONAL' && nextBusinessId) return true;
  if (category.usageType === 'BUSINESS' && !nextBusinessId) return true;
  return false;
};

// Mirrors the QBP transfer remap branch of `updateBusinessId`. Returns the target
// business's equivalent QBP account (matched by type+subType), `UNCATEGORIZED_COA`
// when no equivalent exists or the target is Personal, or `null` when no remap is needed.
export const remapQBPAccountCoaForBusinessChange = ({
  coa,
  accountsById,
  nextBusinessId,
}: {
  coa: ChartOfAccountProps | null | undefined;
  accountsById: AccountLookup | null | undefined;
  nextBusinessId: string | undefined;
}): ChartOfAccountProps | null => {
  if (nextBusinessId === undefined) return null;
  if (coa?.type !== 'ACCOUNT' || !coa.id) return null;
  if (!accountsById) return null;

  const account = accountsById.get(coa.id);
  if (!account || !isQBPAccount(account)) return null;
  if ((account.businessId ?? '') === nextBusinessId) return null;

  if (!nextBusinessId) return UNCATEGORIZED_COA;

  const equivalent = accountsById.find(
    (acct) => acct.businessId === nextBusinessId && acct.type === account.type && acct.subType === account.subType,
  );
  if (equivalent?.id && equivalent.id !== coa.id) {
    return { type: 'ACCOUNT', id: equivalent.id };
  }

  return UNCATEGORIZED_COA;
};

// Returns the replacement coa, or `null` when no change is needed.
export const resolveCoaForBusinessChange = ({
  coa,
  categoriesById,
  accountsById,
  nextBusinessId,
}: {
  coa: ChartOfAccountProps | null | undefined;
  categoriesById: CategoryLookup | null | undefined;
  accountsById: AccountLookup | null | undefined;
  nextBusinessId: string | undefined;
}): ChartOfAccountProps | null => {
  if (nextBusinessId === undefined || !coa) return null;

  if (coa.type === 'CATEGORY') {
    const category = coa.id && categoriesById ? categoriesById.get(coa.id) : undefined;
    return shouldResetCategoryForBusinessChange({ coa, category, nextBusinessId }) ? UNCATEGORIZED_COA : null;
  }

  if (coa.type === 'ACCOUNT') {
    return remapQBPAccountCoaForBusinessChange({ coa, accountsById, nextBusinessId });
  }

  return null;
};

// Returns the original reference when nothing changes, so callers can detect no-op
// updates via identity check.
export function resolveSplitItemCoasForBusinessChange<T extends { coa?: ChartOfAccountProps | null }>(
  splitItems: ReadonlyArray<T> | null | undefined,
  {
    categoriesById,
    accountsById,
    nextBusinessId,
    preserveBusinessCategoryCoasWhenPersonal = false,
  }: {
    categoriesById: CategoryLookup | null | undefined;
    accountsById: AccountLookup | null | undefined;
    nextBusinessId: string | undefined;
    preserveBusinessCategoryCoasWhenPersonal?: boolean;
  },
): ReadonlyArray<T> | null | undefined {
  if (!splitItems || splitItems.length === 0) return splitItems;
  if (nextBusinessId === undefined) return splitItems;

  let changed = false;
  const next = splitItems.map((item) => {
    if (preserveBusinessCategoryCoasWhenPersonal && item.coa?.type === 'CATEGORY') {
      const category = item.coa.id && categoriesById ? categoriesById.get(item.coa.id) : undefined;
      if (category?.usageType === 'BUSINESS' && !nextBusinessId) {
        return item;
      }
    }

    const nextCoa = resolveCoaForBusinessChange({
      coa: item.coa ?? undefined,
      categoriesById,
      accountsById,
      nextBusinessId,
    });
    if (nextCoa) {
      changed = true;
      return { ...item, coa: nextCoa };
    }
    return item;
  });
  return changed ? next : splitItems;
}
